import { Check, MessageCircle, Crown } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { waLink } from "@/lib/site";

const PLANS = [
  { name: "Starter", price: "2,999", ideal: "Ideal for small businesses and startups.", f: ["Up to 5 pages", "Mobile-responsive design", "Contact form", "WhatsApp integration", "Basic SEO setup"] },
  { name: "Gold", price: "4,999", popular: true, ideal: "Ideal for growing businesses.", f: ["Up to 10 pages", "Custom professional design", "Enquiry forms", "Google Maps integration", "On-page SEO basics", "Social media integration"] },
  { name: "Premium", price: "9,999", ideal: "Ideal for businesses requiring advanced functionality.", f: ["Custom page requirements", "Premium UI/UX design", "Advanced features as agreed", "Custom integrations", "Performance optimisation", "SEO-ready structure"] },
];

const Plan = ({ p, i }) => (
  <div
    data-testid={`pricing-card-${p.name.toLowerCase()}`}
    className={`relative flex h-full flex-col rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1.5 sm:p-10 ${
      p.popular
        ? "border border-gold/60 bg-gradient-to-b from-[#1c1a10] via-[var(--navy)] to-[var(--navy)] shadow-[0_40px_100px_-40px_rgba(232,182,76,.45)] lg:-my-6"
        : "border border-white/[0.08] bg-navy/60"
    }`}
  >
    {p.popular && (
      <span data-testid="pricing-most-popular-badge" className="btn-gold absolute -top-3.5 left-8 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">
        <Crown className="h-3.5 w-3.5" /> Most Popular
      </span>
    )}
    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">0{i + 1}</p>
    <h3 className="font-display mt-3 text-2xl font-semibold text-white">{p.name} Website Package</h3>
    <p className="mt-2 text-sm text-[var(--muted)]">{p.ideal}</p>
    <p data-testid={`pricing-price-${p.name.toLowerCase()}`} className="font-display mt-8 flex items-baseline gap-1 text-5xl font-semibold text-gold-grad">
      <span className="text-2xl">₹</span>{p.price}
    </p>
    <div className="my-8 h-px bg-white/10" />
    <ul className="space-y-4">
      {p.f.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${p.popular ? "bg-[var(--gold)] text-black" : "bg-royal/20 text-[var(--royal-2)]"}`}>
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
          {f}
        </li>
      ))}
    </ul>
    <a
      href={waLink(`Hello Pratap Group, I am interested in the ${p.name} Website Package. Please share the best price and details.`)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`pricing-cta-${p.name.toLowerCase()}`}
      className={`btn-shine mt-10 inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 ${p.popular ? "btn-gold" : "border border-white/15 text-white hover:border-[var(--gold)]"}`}
    >
      <MessageCircle className="h-4 w-4" /> Get Best Price
    </a>
  </div>
);

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead align="center" eyebrow="Affordable Website Packages" title={<>Packages that fit <span className="text-gold-grad italic">your budget</span>.</>} />
      <div className="mt-20 grid items-stretch gap-8 lg:grid-cols-3 lg:gap-6">
        {PLANS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1} className="h-full"><Plan p={p} i={i} /></Reveal>
        ))}
      </div>
      <Reveal>
        <p data-testid="pricing-note" className="mx-auto mt-16 max-w-3xl rounded-2xl border border-white/[0.08] bg-white/[0.02] px-6 py-4 text-center text-sm leading-relaxed text-slate-300">
          Note: Final pricing, domain, hosting, maintenance, and third-party service costs depend on project requirements.
        </p>
      </Reveal>
    </div>
  </section>
);
